// HOW A MESH IS DRAWN — textured, or as bare topology.
//
// Lifted out of the mesh viewer when the assembly phase wanted the same modes. It is not a
// preference: the texture is very good at hiding the geometry under it, so a smooth
// photographic surface says nothing about how many triangles describe it or how badly they
// are laid out, and the wireframe is where that is answered.
//
// THERE WAS A THIRD, "Both", drawing the edges over the texture — and it was the one mode
// that answered neither question it was made of. Faint enough to let the surface through, the
// edges are unreadable at any distance a whole part is seen from; strong enough to read, they
// are a green mass with a part somewhere inside it. The assembly phase had already dropped it
// on exactly that argument, leaving one viewer offering a mode its sibling refused.
//
// Takes a loaded root and hangs what it needs off it. Nothing here knows about React, a
// panel, or which viewer is asking.

// 'xray': SEE-THROUGH — the parts drawn faint, so what sits inside them shows (a screw in its
// hole); whatever the caller keeps solid stays as it is. See attachDrawModes' `keepSolid`.
export type DrawMode = 'textured' | 'wireframe' | 'xray';

// AND WHICH OF THE TWO WAS LAST ASKED FOR, remembered for the page rather than for the part.
//
// Wireframe is a way of LOOKING, not a fact about a mesh: somebody who has switched to it is
// checking triangulation, and the next part they open is the next thing they want to check. It
// lived in the viewer, which is rebuilt whenever the part changes — so stepping through parts
// with the arrow keys dropped back to textured on every press, and the comparison being made
// had to be set up again each time.
//
// A plain variable and not a store: one viewer is on screen at a time (the panel's is torn
// down while the modal is open), so nothing has to be told when this changes — the next viewer
// to mount reads it in its initialiser. It is not written down anywhere: a preference about
// how to look at a mesh for the next few minutes is not a fact about a document.
let drawn: DrawMode = 'textured';
export const lastDrawMode = (): DrawMode => drawn;
export const rememberDrawMode = (mode: DrawMode): void => { drawn = mode; };

export type DrawModes = {
  apply: (mode: DrawMode) => void;
  // What the mesh actually is, counted while walking it — the number a decimation target is
  // aiming at, and the one the caller would otherwise walk the tree a second time to get.
  vertices: number;
  triangles: number;
};

// The one colour a wireframe is drawn in where nobody says otherwise — dark enough to read
// on the white the meshes are lit against.
const WIRE = 0x0f766e;


// HOW MUCH OF ITS OWN COLOUR THE INSIDE OF A PART KEEPS. Dark enough to read as the shaded
// depth of a cut and not as another lit surface, light enough that the wood is still wood.
const INSIDE = 0.45;

// A PART LOOKED AT FROM INSIDE IS SOLID TIMBER, so it is drawn as solid timber.
//
// The camera can be got inside a part — the near plane cuts the near wall away — and what was
// there was the far wall's INNER surface, lit, textured, with the background showing through
// every gap. Every cue in that picture says hollow, of a part that is not, and it is the same
// picture whether the mesh is a solid or a shell.
//
// A REAL CROSS-SECTION IS A DIFFERENT JOB. Capping the cut needs an actual clipping plane to
// cap against — the near plane is not one three will hand out — and the stencil trick that
// fills it counts front faces against back faces, so it needs a closed surface with no
// duplicate skins. This rail has a duplicate skin over half of itself and the legs frame has
// forty-three non-manifold junctions: the cap would be exact on the meshes that least need it
// and wrong on the ones that do.
//
// So: no cap, and no lighting either. A back face keeps its own diffuse colour and loses
// everything that makes a surface read as one — the shading, the highlight, the sense of which
// way it faces. What is left is a flat dark mass the colour of the part, which is what the
// inside of a piece of wood looks like.
//
// PATCHED RATHER THAN DUPLICATED. The alternative is a second mesh over the same geometry with
// a back-facing material, which doubles the draw calls and then has to be kept in step with
// every other thing that touches these meshes — the wireframe, the piece highlighting, the
// hiding, the picking. This is four lines inside the shader that is already running.
// ONE WIREFRAME PER GEOMETRY, for the page: a mesh is read once and shared by every copy of it
// (see loadCopy), so its edges can be too. Built per scene, Phase 6 drew twenty-one of them, twelve
// million points, on every sub-step, and measured each one's box from scratch to frame the shot.
// The box is the mesh's own: the edges are its vertices.
const wireframes = new WeakMap<import('three').BufferGeometry, import('three').BufferGeometry>();
const wireOf = (THREE: typeof import('three'), geom: import('three').BufferGeometry) => {
  let wire = wireframes.get(geom);
  if (!wire) {
    wire = new THREE.WireframeGeometry(geom);
    if (!geom.boundingBox) geom.computeBoundingBox();
    wire.boundingBox = geom.boundingBox!.clone();
    wireframes.set(geom, wire);
  }
  return wire;
};

const flatten = (mat: import('three').Material): void => {
  mat.onBeforeCompile = (shader) => {
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <opaque_fragment>',
      // BEFORE tone mapping and the colour-space conversion, which is why it replaces this
      // chunk and not a later one: written after those, a colour has to be pre-converted by
      // hand and is a shade off everything around it.
      `#include <opaque_fragment>
      if ( !gl_FrontFacing ) gl_FragColor = vec4( diffuseColor.rgb * ${INSIDE}, diffuseColor.a );`,
    );
  };
  mat.needsUpdate = true;
};

export function attachDrawModes(
  THREE: typeof import('three'),
  root: import('three').Object3D,
  // WHAT COLOUR THIS MESH'S EDGES ARE, asked of each mesh every time a mode is applied — so a
  // caller whose colours depend on where the parts stand answers for the picture being drawn.
  //
  // A viewer holding ONE part has no use for it: there is nothing to tell that part apart
  // from. A viewer holding two has every use — wireframes do not occlude, so a screw and the
  // washer around it become one continuous mass of lines, and the question the mode exists to
  // answer ("where does this part end") is the one thing the picture then cannot say. Which
  // part is which is the caller's business, so the answer comes from the caller.
  wireColour?: (mesh: import('three').Object3D) => number,
  // WHICH MESHES STAY SOLID when seen through — the hardware inside the parts. Asked once per mesh.
  keepSolid?: (mesh: import('three').Object3D) => boolean,
): DrawModes {
  let vertices = 0;
  let triangles = 0;
  const wires: { edges: import('three').LineSegments; mesh: import('three').Object3D }[] = [];
  const solids: import('three').Mesh[] = [];
  const faint: import('three').Mesh[] = [];
  const unwired: import('three').Mesh[] = [];
  root.traverse((child) => {
    const mesh = child as import('three').Mesh;
    if (!mesh.isMesh) return;
    solids.push(mesh);
    if (!keepSolid?.(mesh)) faint.push(mesh);
    const geom = mesh.geometry;
    vertices += geom.attributes.position?.count ?? 0;
    triangles += geom.index
      ? geom.index.count / 3
      : (geom.attributes.position?.count ?? 0) / 3;
    // Pushed away from the camera by a hair so the edges sit ON the surface instead of
    // fighting it — without this the lines dash and shimmer as the object turns, which
    // reads as a broken mesh rather than as a rendering artefact.
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    mats.forEach((m) => {
      const mat = m as import('three').MeshStandardMaterial;
      mat.polygonOffset = true;
      mat.polygonOffsetFactor = 1;
      mat.polygonOffsetUnits = 1;
      // AND BOTH SIDES OF EVERY FACE, so the part does not vanish when the camera goes
      // inside it.
      //
      // glTF materials are single-sided by default, which three reads as `FrontSide`: each
      // triangle is drawn only from its outward face. Zoom into a part and every face turns
      // its back, back faces are not drawn at all, and the surface disappears — you see the
      // white background through a solid mesh. Nothing is missing from the geometry; it is
      // the renderer, and it is the commonest way a good mesh looks broken.
      //
      // Three flips the normal on a back face by itself, so the inside lights properly
      // rather than coming back black.
      //
      // WHAT IT COSTS, said plainly because it is not free. Culling off means the part can
      // pay up to twice the fragment work — small on these meshes, real on a weak GPU. And
      // it hides a diagnostic: while faces were single-sided, being unable to see the
      // interior was the proof that the winding was outward, and an inside-out part now
      // looks exactly like a right-way-round one. `close_surface` reports the winding
      // instead, which is where that answer belongs.
      mat.side = THREE.DoubleSide;
      flatten(mat);
    });
    // ITS EDGES ARE BUILT THE FIRST TIME WIREFRAME IS ASKED FOR, not here (see wireUp).
    unwired.push(mesh);
  });

  // THE EDGES, BUILT ONCE AND ONLY WHEN WIREFRAME IS FIRST SHOWN. Built up front, they were two
  // seconds a scene of a dining chair, on the one thread — the published page's four colourway
  // tiles queued eight seconds behind them, and that page has no wireframe to show (Louis
  // 2026-10-23).
  const wireUp = () => {
    for (const mesh of unwired.splice(0)) {
      // A SECOND OBJECT rather than a flag on the first: LineSegments over the same geometry
      // gives real one-pixel edges, where a wireframe material would give triangle outlines
      // that thicken with the camera.
      //
      // It hangs off the mesh, which is why the modes below hide a MATERIAL and never an
      // object: `mesh.visible = false` hides the whole subtree, wires included, and wireframe
      // mode drew a blank panel because of it. Setting `material.visible` drops that mesh's
      // own draw call and leaves its children rendering, which is exactly the difference
      // wanted.
      const wire = new THREE.LineSegments(
        wireOf(THREE, mesh.geometry),
        new THREE.LineBasicMaterial({ transparent: true }),
      );
      wire.visible = false;
      // AND NEVER THE THING A RAY FINDS. It is the same geometry as the mesh it hangs on, drawn
      // a second time as edges, so it has nothing of its own to be hit: whatever wants the part
      // wants the surface under this.
      //
      // Left pickable it does not merely add a duplicate answer, it adds a WRONG one. A line has
      // no surface to cross, so three reports the nearest point ON THE EDGE within a threshold
      // of the ray — a point that is not on the ray at all. The hole marks were placed by such a
      // hit and came out on the far side of the part: the ray was cast down a face's normal and
      // the answer came back several centimetres across it.
      wire.raycast = () => {};
      mesh.add(wire);
      // Asked BEFORE the wire is a child of anything else, and asked about the mesh rather than
      // about the wire: the caller answers by finding which part the mesh belongs to, and the
      // wire is not a part.
      wires.push({ edges: wire, mesh });
    }
  };

  const apply = (mode: DrawMode) => {
    const wire = mode === 'wireframe';
    if (wire) wireUp();
    solids.forEach((mesh) => {
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      mats.forEach((mat) => { mat.visible = !wire; });
    });
    // SEEN THROUGH: faint, and not hiding what is behind — the depth left unwritten, so a screw
    // inside is drawn whole. Put back exactly as it was on the way out.
    const see = mode === 'xray';
    faint.forEach((mesh) => {
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      mats.forEach((mat) => {
        const was = (mat.userData.solid ??= {
          transparent: mat.transparent, opacity: mat.opacity, depthWrite: mat.depthWrite,
        }) as { transparent: boolean; opacity: number; depthWrite: boolean };
        mat.transparent = see || was.transparent;
        mat.opacity = see ? 0.3 : was.opacity;
        mat.depthWrite = see ? false : was.depthWrite;
        mat.needsUpdate = true;
      });
    });
    wires.forEach(({ edges, mesh }) => {
      const colour = wireColour?.(mesh) ?? WIRE;
      edges.visible = wire;
      // Nothing occludes the far side, so the edges carry the whole shape on their own:
      // solid, and dark enough to read on white. They were also drawn faint over the
      // texture, for a third mode that no longer exists.
      const mat = edges.material as import('three').LineBasicMaterial;
      mat.color.set(colour);
      mat.opacity = 1;
    });
  };

  return { apply, vertices, triangles: Math.round(triangles) };
}
